import React from 'react';

import {Button, CheckIcon, ModalPopup} from '@kit';

import {useCharacter} from '../../../character-context';
import {resourceKey} from '../../../model/procedures';
import type {SpellSlot} from '../../../model/spell-slots';
import {useFeatureAction} from '../use-feature-action';
import type {ProcedureModalProps} from './procedure-modal-props';

import css from '../../sheet.module.css';

const RESURGENCE_SLOT_LEVEL = 1;

/**
 * Дикое восстановление: без использований Дикого облика можно потратить ячейку и получить одно;
 * раз до Долгого отдыха использование Дикого облика превращается в ячейку 1-го круга.
 */
export const WildResurgenceModal: React.FC<ProcedureModalProps<'wildResurgence'>> = ({action, procedure, onClose}) => {
	const {character} = useCharacter();
	const {spend, record, canUse} = useFeatureAction(action);
	const key = resourceKey(procedure.resource);
	const wildShape = character.limitedUse(key);
	const wildShapeLeft = wildShape ? wildShape.max - wildShape.spent : 0;
	const slots = character.spellSlots.filter((slot) => !slot.pact && slot.spent < slot.max);
	const firstLevelSlot = character.spellSlots.find((slot) => !slot.pact && slot.level === RESURGENCE_SLOT_LEVEL);

	const slotToWildShape = (slot: SpellSlot) => {
		record(`ячейка ${slot.level} круга → ${wildShape?.title}: +1`, (current) => ({
			spentSpellSlots: {...current.spentSpellSlots, [slot.key]: (current.spentSpellSlots[slot.key] || 0) + 1},
			spentUses: {...current.spentUses, [key]: Math.max(0, (current.spentUses[key] || 0) - 1)},
		}));
		onClose();
	};

	const wildShapeToSlot = () => {
		if (!firstLevelSlot) return;
		spend(`${wildShape?.title}: −1 → ячейка ${RESURGENCE_SLOT_LEVEL} круга`, (current) => ({
			spentUses: {...current.spentUses, [key]: (current.spentUses[key] || 0) + 1},
			spentSpellSlots: {
				...current.spentSpellSlots,
				[firstLevelSlot.key]: (current.spentSpellSlots[firstLevelSlot.key] || 0) - 1,
			},
		}));
		onClose();
	};

	return (
		<ModalPopup open onClose={onClose} header={action.title} maxWidth={520}>
			<p>
				{wildShape?.title}: осталось <b>{wildShapeLeft}</b> из {wildShape?.max ?? 0}.
			</p>

			<h3 className={css.groupTitle}>Ячейка → {wildShape?.title}</h3>
			{wildShapeLeft > 0 ? (
				<p className={css.muted}>Можно, только когда использований Дикого облика не осталось.</p>
			) : (
				<div className={css.formRows}>
					{slots.map((slot) => (
						<div key={slot.key} className={css.formRow}>
							<span>
								{slot.level} круг <span className={css.muted}>· свободно {slot.max - slot.spent}</span>
							</span>
							<Button size="small" onClick={() => slotToWildShape(slot)}>
								Потратить
							</Button>
						</div>
					))}
				</div>
			)}

			<h3 className={css.groupTitle}>
				{wildShape?.title} → ячейка {RESURGENCE_SLOT_LEVEL} круга
			</h3>
			<p className={css.muted}>Раз до Долгого отдыха.</p>
			<div className={css.modalButtons}>
				<Button disabled={!canUse || !wildShapeLeft || !firstLevelSlot} onClick={wildShapeToSlot}>
					Получить ячейку
				</Button>
				<Button appearance="primary" icon={<CheckIcon />} onClick={onClose}>
					Готово
				</Button>
			</div>
		</ModalPopup>
	);
};
